<template>
  <div>
    <v-btn text color="primary" class="ml-2" @click="dialog = true"
      >Add Item</v-btn
    >
    <v-dialog v-model="dialog" persistent width="600">
      <v-card>
        <v-card-title>Add Item</v-card-title>
        <v-card-text>
          <div v-if="ready">
            <v-row>
              <v-col
                ><v-text-field label="Search" v-model="search"></v-text-field
              ></v-col>
            </v-row>
            <div v-if="selected_item">
              <div class="d-flex">
                <div class="ma-3">
                  <p>Item Name:</p>
                  <h3 class="mt-n5">
                    {{ selected_item.item_name }}
                  </h3>
                </div>
                <div class="ma-3">
                  <p>Item Amount:</p>
                  <h3 class="mt-n5">
                    {{ selected_item.selling_price }}
                  </h3>
                </div>
              </div>
              <v-row>
                <v-col>
                  <v-text-field
                    label="Quantity"
                    type="number"
                    min="1"
                    v-model="form.quantity"
                    @input="onQuantityChange"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field
                    label="Total Price"
                    v-model="total_price"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- BATCH ALLOCATION (FIFO) -->
              <v-row>
                <v-col cols="12">
                  <div class="d-flex align-center mb-1">
                    <span class="caption font-weight-bold"
                      >Batch Allocation</span
                    >
                    <span class="caption grey--text ml-2"
                      >(FIFO by default)</span
                    >
                    <v-spacer></v-spacer>
                    <v-progress-circular
                      v-if="fifo_loading"
                      indeterminate
                      size="16"
                      width="2"
                      color="primary"
                      class="mr-2"
                    ></v-progress-circular>
                    <v-btn
                      x-small
                      text
                      color="primary"
                      :disabled="!can_pick_batches"
                      @click="openLotPicker"
                      >Edit Batch</v-btn
                    >
                  </div>
                  <div v-if="allocations.length">
                    <v-chip
                      v-for="alloc in allocations"
                      :key="alloc.lot_id"
                      small
                      color="blue"
                      dark
                      class="mr-1 mb-1"
                    >
                      Batch {{ alloc.lot_id
                      }}{{ alloc.batch_name ? " - " + alloc.batch_name : "" }} ×
                      {{ alloc.allocate_qty }}
                    </v-chip>
                  </div>
                  <div v-else class="caption grey--text">
                    No batch allocation yet — enter a quantity.
                  </div>
                </v-col>
              </v-row>

              <!-- SERIAL NUMBERS (JMB-242) -->
              <v-row v-if="selected_item.is_serialized">
                <v-col cols="12">
                  <v-autocomplete
                    v-model="selected_serials"
                    :items="serial_options"
                    :loading="serials_loading"
                    label="Serial Numbers — scan or search in-stock serials"
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    outlined
                    dense
                    prepend-inner-icon="mdi-barcode-scan"
                  ></v-autocomplete>
                  <div
                    class="caption mt-n4"
                    :class="
                      selected_serials.length === (parseInt(form.quantity) || 0)
                        ? 'green--text'
                        : 'orange--text'
                    "
                  >
                    {{ selected_serials.length }} /
                    {{ parseInt(form.quantity) || 0 }} selected
                  </div>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-textarea
                    label="Remarks"
                    v-model="form.remarks"
                    rows="2"
                    outlined
                    dense
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="2">
                  <v-btn color="" :loading="loading" @click="insert_item"
                    >Add</v-btn
                  >
                </v-col>
              </v-row>
            </div>
            <v-data-table
              :items-per-page="5"
              :search="search"
              :items="items"
              :headers="items_header"
            >
              <template v-slot:item.action="{ item }">
                <v-btn
                  small
                  :color="payment_type == 'Item' ? 'warning' : 'primary'"
                  @click="add(item)"
                  >Select</v-btn
                >
              </template>
            </v-data-table>
          </div>
          <div v-else>
            <h2 class="text-center"></h2>
            Loading Data...
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- LOT PICKER DIALOG -->
    <v-dialog v-model="lot_picker_dialog" max-width="700" persistent>
      <v-card>
        <v-card-title>
          Edit Batch Allocation —
          {{ selected_item ? selected_item.item_name : "" }}
          <v-spacer></v-spacer>
          <span class="caption grey--text"
            >Required: {{ lot_picker_required_qty }}</span
          >
        </v-card-title>
        <v-card-text>
          <v-alert v-if="lot_picker_error" type="error" dense class="mb-2">{{
            lot_picker_error
          }}</v-alert>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Batch</th>
                <th>Supplier</th>
                <th>Purchase Date</th>
                <th>Unit Cost</th>
                <th>In Stock</th>
                <th style="width: 110px">Qty to Allocate</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="lot in lot_picker_lots" :key="lot.id">
                <td>
                  Batch {{ lot.id
                  }}{{
                    lot.import_batch
                      ? " - " + lot.import_batch.batch_description
                      : ""
                  }}
                </td>
                <td>{{ lot.supplier || "-" }}</td>
                <td>
                  {{
                    lot.purchase_date ? lot.purchase_date.substring(0, 10) : "-"
                  }}
                </td>
                <td>
                  {{
                    lot.unit_cost ? Number(lot.unit_cost).toLocaleString() : "-"
                  }}
                </td>
                <td>{{ lot.remaining_quantity }}</td>
                <td>
                  <v-text-field
                    dense
                    type="number"
                    v-model.number="lot._allocate_qty"
                    :max="lot.remaining_quantity"
                    min="0"
                    hide-details
                    style="width: 90px"
                  ></v-text-field>
                </td>
              </tr>
              <tr v-if="!lot_picker_lots.length">
                <td colspan="6" class="text-center grey--text">
                  No stock batches available for this item.
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="5" class="text-right font-weight-bold">
                  Total Allocated:
                </td>
                <td
                  :class="{
                    'red--text': lot_picker_total !== lot_picker_required_qty,
                    'green--text': lot_picker_total === lot_picker_required_qty,
                  }"
                  class="font-weight-bold"
                >
                  {{ lot_picker_total }} / {{ lot_picker_required_qty }}
                </td>
              </tr>
            </tfoot>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="lot_picker_dialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveLotPicker">Save Allocation</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
export default {
  props: ["invoice_id", "invoice_code"],
  async created() {
    this.check_if_exist();
    this.ready = true;
  },
  methods: {
    add(item) {
      this.selected_item = item;
      this.form.quantity = "";
      this.allocations = [];
      this.selected_serials = [];
      this.serial_options = [];
      if (item.is_serialized) {
        this.loadSerialOptions(item.id);
      }
    },

    async loadSerialOptions(itemId) {
      this.serials_loading = true;
      try {
        const response = await axios.get(`/item/${itemId}/serials`);
        this.serial_options = response.data
          .filter((s) => s.status === "in_stock")
          .map((s) => s.serial_number);
      } catch (e) {
        console.error("Failed to load serials", e);
        this.serial_options = [];
      }
      this.serials_loading = false;
    },

    onQuantityChange() {
      clearTimeout(this.fifo_timer);
      this.fifo_timer = setTimeout(() => {
        this.fetchFIFOPreview();
      }, 500);
    },

    async fetchFIFOPreview() {
      const qty = parseInt(this.form.quantity);
      if (!this.selected_item || !qty || qty <= 0) {
        this.allocations = [];
        return;
      }
      this.fifo_loading = true;
      try {
        const response = await axios.post("/invoice/fifo_preview", {
          items: [{ item_id: this.selected_item.id, quantity: qty }],
        });
        const preview = response.data[this.selected_item.id];
        this.allocations =
          preview && preview.allocations ? preview.allocations : [];
      } catch (e) {
        console.error("FIFO preview failed:", e);
        this.allocations = [];
      }
      this.fifo_loading = false;
    },

    openLotPicker() {
      this.lot_picker_required_qty = parseInt(this.form.quantity) || 0;
      this.lot_picker_error = "";

      const activeLots = this.selected_item.active_stock_lots || [];
      this.lot_picker_lots = activeLots.map((lot) => {
        const existing = this.allocations.find((a) => a.lot_id === lot.id);
        return {
          ...lot,
          purchase_date: lot.purchase_date
            ? lot.purchase_date.substring(0, 10)
            : null,
          _allocate_qty: existing ? existing.allocate_qty : 0,
        };
      });

      this.lot_picker_dialog = true;
    },

    saveLotPicker() {
      const total = this.lot_picker_lots.reduce(
        (sum, lot) => sum + (parseInt(lot._allocate_qty) || 0),
        0
      );
      if (total !== this.lot_picker_required_qty) {
        this.lot_picker_error = `Total allocated (${total}) must equal required quantity (${this.lot_picker_required_qty})`;
        return;
      }

      for (const lot of this.lot_picker_lots) {
        const qty = parseInt(lot._allocate_qty) || 0;
        if (qty > lot.remaining_quantity) {
          this.lot_picker_error = `Batch #${lot.id} only has ${lot.remaining_quantity} in stock`;
          return;
        }
      }

      this.allocations = this.lot_picker_lots
        .filter((lot) => (parseInt(lot._allocate_qty) || 0) > 0)
        .map((lot) => ({
          lot_id: lot.id,
          batch_name: lot.import_batch
            ? lot.import_batch.batch_description
            : null,
          supplier: lot.supplier,
          purchase_date: lot.purchase_date,
          unit_cost: lot.unit_cost,
          remaining_quantity: lot.remaining_quantity,
          allocate_qty: parseInt(lot._allocate_qty),
        }));

      this.lot_picker_dialog = false;
    },

    async insert_item() {
      // Client-side stock validation
      if (
        this.selected_item &&
        this.form.quantity > this.selected_item.quantity
      ) {
        this.$alert({
          title: "Validation",
          message: `Insufficient stock for ${this.selected_item.item_name}. Available: ${this.selected_item.quantity}, Requested: ${this.form.quantity}`,
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      // Serialized items must have exactly qty serials selected (JMB-242)
      if (this.selected_item && this.selected_item.is_serialized) {
        const qty = parseInt(this.form.quantity) || 0;
        if (this.selected_serials.length !== qty) {
          this.$alert({
            title: "Serial Numbers",
            message: `${this.selected_item.item_name} is serialized: select ${qty} serial number(s) (${this.selected_serials.length} selected).`,
            color: "warning",
            icon: "mdi-barcode",
          });
          return;
        }
      }

      // Ensure a batch allocation exists (default to FIFO preview)
      if (!this.allocations.length) {
        await this.fetchFIFOPreview();
      }

      // Validate the allocation covers the requested quantity
      const qty = parseInt(this.form.quantity) || 0;
      if (this.total_allocated !== qty) {
        this.$alert({
          title: "Validation",
          message: `Batch allocation (${this.total_allocated}) does not match the requested quantity (${qty}). Please edit the batch allocation.`,
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      let x = await this.$confirm({
        title: "Add Item",
        message: "Are you sure you want to add this item?",
        color: "primary",
        icon: "mdi-plus",
      });
      if (x) {
        this.form.invoice_id = this.invoice_id;
        this.form.id = this.selected_item.id;
        this.form.total_amount = this.total_price;
        this.form.remarks = this.form.remarks || "";
        this.form.added_by = this.user ? this.user.full_name : "";
        this.form.date_added = new Date().toISOString().slice(0, 10);
        // Batch allocations keyed by item id (FIFO by default, user-editable)
        this.form.lot_allocations = {
          [this.selected_item.id]: this.allocations.map((a) => ({
            lot_id: a.lot_id,
            quantity: a.allocate_qty,
          })),
        };
        // Selected serials for serialized items (JMB-242)
        this.form.serials = this.selected_item.is_serialized
          ? [...this.selected_serials]
          : [];

        this.loading = true;
        this.add_item_to_invoice_items(this.form)
          .then((updatedInvoice) => {
            this.$alert({
              title: "Success",
              message: "Successfully added a new item!",
              color: "success",
              icon: "mdi-check-circle",
            });
            this.$emit("item-added", updatedInvoice);
            this.loading = false;
            this.dialog = false;
            this.selected_item = null;
            this.form.quantity = "";
            this.form.remarks = "";
            this.allocations = [];
            this.selected_serials = [];
            this.serial_options = [];
          })
          .catch((err) => {
            this.loading = false;
            // Handle 422 stock validation errors from server
            if (
              err.response &&
              err.response.status === 422 &&
              err.response.data.message
            ) {
              this.$alert({
                title: "Error",
                message: err.response.data.message,
                color: "error",
                icon: "mdi-alert-circle",
              });
              return;
            }
            this.$alert({
              title: "Error",
              message: String(err),
              color: "error",
              icon: "mdi-alert-circle",
            });
          });
      }
    },
    change_items() {
      console.log(this.selected_item);
    },
    check_if_exist() {
      if (!this.items) this.get_items();
    },
    ...mapActions({
      get_items: "item/get_items",
      add_item_to_invoice_items: "invoice/add_item_to_invoice_items",
      delete_payable_from_invoice: "invoice/delete_payable_from_invoice",
    }),
  },
  computed: {
    ...mapGetters({
      items: "item/items",
      services: "service/services",
      user: "auth/user",
    }),
    total_price() {
      let amount = this.selected_item.service_amount
        ? this.selected_item.service_amount
        : this.selected_item.selling_price;

      return amount * this.form.quantity;
    },
    total_allocated() {
      return this.allocations.reduce(
        (sum, a) => sum + (parseInt(a.allocate_qty) || 0),
        0
      );
    },
    can_pick_batches() {
      const qty = parseInt(this.form.quantity) || 0;
      return qty > 0 && !this.fifo_loading;
    },
    lot_picker_total() {
      return this.lot_picker_lots.reduce(
        (sum, lot) => sum + (parseInt(lot._allocate_qty) || 0),
        0
      );
    },
  },
  data() {
    return {
      loading: false,
      form: {
        quantity: "",
        remarks: "",
      },
      items_header: [
        { text: "Action", value: "action" },
        { text: "ID", value: "id" },
        { text: "Item Name", value: "item_name" },
        { text: "Selling Price", value: "selling_price" },
        { text: "Stock", value: "quantity" },
      ],
      search: "",
      selected_item: null,
      ready: false,
      payment_type: "Items",
      dialog: false,
      // Batch allocation (FIFO) state
      allocations: [],
      fifo_loading: false,
      fifo_timer: null,
      lot_picker_dialog: false,
      lot_picker_lots: [],
      lot_picker_required_qty: 0,
      lot_picker_error: "",
      // Serial selection (JMB-242)
      selected_serials: [],
      serial_options: [],
      serials_loading: false,
    };
  },
};
</script>

<style lang="scss" scoped></style>
